import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Client, ClientContact } from '../../types';
import Input from './Input';
import Select from './Select';
import TextArea from './TextArea';
import Button from './Button';

interface ClientFormProps {
  client?: Client;
  onSubmit: (data: Partial<Client>) => void;
  onCancel: () => void;
}

interface ClientFormData {
  name: string;
  address: string;
  status: 'active' | 'inactive';
  contacts: ClientContact[];
}

const emptyContact: ClientContact = { contactPerson: '', email: '', phone: '' };

const buildInitialContacts = (client?: Client): ClientContact[] => {
  if (client?.contacts && Array.isArray(client.contacts) && client.contacts.length > 0) {
    return client.contacts.map((c) => ({
      contactPerson: c.contactPerson || '',
      email: c.email || '',
      phone: c.phone || '',
    }));
  }
  // Fall back to legacy single-contact fields
  if (client && (client.contactPerson || client.email || client.phone)) {
    return [{
      contactPerson: client.contactPerson || '',
      email: client.email || '',
      phone: client.phone || '',
    }];
  }
  return [{ ...emptyContact }];
};

const ClientForm: React.FC<ClientFormProps> = ({ client, onSubmit, onCancel }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ClientFormData>({
    defaultValues: {
      name: client?.name || '',
      address: client?.address || '',
      status: client?.status || 'active',
      contacts: buildInitialContacts(client),
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'contacts' });

  const handleFormSubmit = (data: ClientFormData) => {
    // Drop rows where every field is empty
    const contacts = (data.contacts || [])
      .map((c) => ({
        contactPerson: c.contactPerson?.trim() || '',
        email: c.email?.trim() || '',
        phone: c.phone?.trim() || '',
      }))
      .filter((c) => c.contactPerson || c.email || c.phone);

    const primary = contacts[0];

    const submitData: Partial<Client> = {
      name: data.name,
      address: data.address,
      status: data.status,
      contacts,
      // Keep legacy single-contact fields in sync with the first contact
      contactPerson: primary?.contactPerson || '',
      email: primary?.email || '',
      phone: primary?.phone || '',
      id: client?.id,
      documentId: client?.documentId,
      createdAt: client?.createdAt || new Date().toISOString()
    };

    onSubmit(submitData);
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {client ? 'Edit Client' : 'Add New Client'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <Input
          label="Client Name"
          {...register('name', {
            required: 'Client name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' }
          })}
          error={errors.name?.message}
          placeholder="Enter client company name"
        />

        {/* Contacts */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">Contacts</label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => append({ ...emptyContact })}
            >
              + Add Contact
            </Button>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className="border border-gray-200 rounded-lg p-4 relative">
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-600"
                  title="Remove contact"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Contact Person"
                  {...register(`contacts.${index}.contactPerson`)}
                  placeholder="Enter contact person name"
                />
                <Input
                  label="Email"
                  type="email"
                  {...register(`contacts.${index}.email`, {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  error={errors.contacts?.[index]?.email?.message}
                  placeholder="contact@client.com"
                />
                <Input
                  label="Phone"
                  {...register(`contacts.${index}.phone`, {
                    pattern: {
                      value: /^[+]?[0-9\s\-()]{4,20}$/,
                      message: 'Invalid phone number'
                    }
                  })}
                  error={errors.contacts?.[index]?.phone?.message}
                  placeholder="+1-555-0123"
                />
              </div>
            </div>
          ))}
        </div>

        <TextArea
          label="Address"
          {...register('address', {
            required: 'Address is required',
            minLength: { value: 10, message: 'Address must be at least 10 characters' }
          })}
          error={errors.address?.message}
          placeholder="Enter complete client address"
          rows={3}
        />

        <Select
          label="Status"
          {...register('status', { required: 'Status is required' })}
          options={statusOptions}
          error={errors.status?.message}
        />

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {client ? 'Update Client' : 'Create Client'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
